import { createContext, useContext, useState, useCallback, useMemo, useRef, type ReactNode } from 'react';
import { type Locale, type TranslateFn, detectLocale, persistLocale, createTranslate } from '@/i18n';

interface I18nContextType {
  t: TranslateFn;
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

const I18nContext = createContext<I18nContextType>({
  t: createTranslate('en'),
  locale: 'en',
  setLocale: () => {},
});

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(detectLocale);
  const overlayRef = useRef<HTMLDivElement>(null);

  const setLocale = useCallback((l: Locale) => {
    const el = overlayRef.current;
    if (el) {
      el.style.opacity = '1';
      setTimeout(() => {
        persistLocale(l);
        setLocaleState(l);
        setTimeout(() => { el.style.opacity = '0'; }, 60);
      }, 120);
    } else {
      persistLocale(l);
      setLocaleState(l);
    }
  }, []);

  const t = useMemo(() => createTranslate(locale), [locale]);

  return (
    <I18nContext.Provider value={{ t, locale, setLocale }}>
      {children}
      <div
        ref={overlayRef}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'var(--color-bg-base, #0c0d12)',
          opacity: 0,
          pointerEvents: 'none',
          transition: 'opacity 150ms ease-in-out',
          zIndex: 9999,
        }}
      />
    </I18nContext.Provider>
  );
}

export const useI18n = () => useContext(I18nContext);
